import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';

import { AppGlobals } from '../../app.globals';

/**
 * Blocks the /config subtree for anyone who is not an administrator.
 *
 * The drawer already hides the admin entries, but that is cosmetic — before this guard the
 * pages were reachable by typing the URL. The server is the real authority (the admin
 * endpoints require EnumPermissions.Admin); this only keeps non-admins out of a page that
 * would answer every request with "Administrator rights required".
 *
 * Awaits $userPermissions rather than reading AppGlobals.UserPermissions directly: on a cold
 * deep link the guard runs before app.component's GetUserDetails resolves, and the seeded
 * all-false object would bounce a genuine admin back to the dashboard.
 *
 * While the user is signed out the promise stays pending and that navigation is simply lost —
 * MSAL's loginRedirect tears the document down. app.component.login() is what preserves the
 * deep link, and it has to read location.path() rather than router.url precisely because this
 * guard leaves the navigation uncommitted.
 */
export const adminGuard: CanActivateFn = async () => {
  const router = inject(Router);

  const permissions = await firstValueFrom(
    AppGlobals.$userPermissions.pipe(filter(p => p != null))
  );

  return permissions.isAdministrator === true || router.createUrlTree(['/']);
};
